/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import CurrentPrice from '../../../app/components/display-price/current-price'
import ListPrice from '../../../app/components/display-price/list-price'

/**
 * @param priceData - price info extracted from a product
 * // If a product is a set,
 *      on PLP, Show From X where X is the lowest of its children
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PLP and PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show exact price on PLP and PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param labelForA11y - label to be used for a11y
 */
const DisplayPrice = ({
    labelForA11y,
    priceData,
    currency,
    quantity = 1,
    currentPriceProps = {},
    listPriceProps = {},
    isPLPPage,
    promotions
}) => {
    const {listPrice, currentPrice, isASet, isMaster, isOnSale, isRange, maxPrice} = priceData
    const hasSale = promotions?.some((item) => item.isSale === true)
    const renderCurrentPrice = (isRange) =>
        currentPrice && (
            <CurrentPrice
                labelForA11y={labelForA11y}
                price={currentPrice * quantity}
                as="b"
                currency={currency}
                isRange={isRange}
                color={hasSale || isOnSale ? 'pomegranateBase' : 'textColor'}
                {...currentPriceProps}
                fontSize={isPLPPage ? {base: '16px'} : '24px'}
                lineHeight={isPLPPage ? undefined : '24px'}
                fontWeight={'extrabold'}
                mr={{base: '4px', sm: '8px'}}
            />
        )

    const renderCurrentListPrice = () =>
        currentPrice && (
            <CurrentPrice
                labelForA11y={labelForA11y}
                price={maxPrice || listPrice}
                as="b"
                currency={currency}
                isRange={false}
                {...currentPriceProps}
                color={'grey25'}
                textDecorationLine="line-through"
                fontSize={isPLPPage ? {base: '12px'} : '16px'}
                fontWeight={{base: 500}}
                lineHeight={'16px'}
            />
        )

    const renderListPrice = (isRange) =>
        listPrice && (
            <ListPrice
                labelForA11y={labelForA11y}
                currency={currency}
                price={listPrice * quantity}
                isRange={isRange}
                {...listPriceProps}
                fontSize={isPLPPage ? {base: '12px'} : '16px'}
                fontWeight={{base: 500}}
            />
        )

    const renderPriceSet = (isRange) => {
        return isMaster && isRange && (maxPrice || listPrice) !== currentPrice ? (
            <>
                {renderCurrentPrice(isRange)} {renderCurrentListPrice()}
            </>
        ) : (
            <>
                {renderCurrentPrice(isRange)} {isOnSale && renderListPrice(isRange)}
            </>
        )
    }

    if (isASet) {
        return <Box>{renderCurrentPrice(true)}</Box>
    }

    if (isMaster) {
        return (
            <Box display={'flex'} alignItems={'center'}>
                {renderPriceSet(isRange)}
            </Box>
        )
    }

    return (
        <Box display={'flex'} alignItems={'center'}>
            {renderPriceSet(false)}
        </Box>
    )
}

DisplayPrice.propTypes = {
    priceData: PropTypes.object,
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    currentPriceProps: PropTypes.object,
    listPriceProps: PropTypes.object,
    quantity: PropTypes.number,
    isPLPPage: PropTypes.bool,
    promotions: PropTypes.array
}

export default DisplayPrice
